import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Lock, BookOpen, Wand2, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { usePlayer } from "@/context/PlayerContext";
import { GRIMOIRE_PAGES } from "@/data/grimoire";
import { generateGrimoirePage } from "@/lib/api";
import { toast } from "sonner";
import { useState } from "react";
import { localizeGrimoirePage } from "@/i18n/game";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import SoundToggle from "@/components/SoundToggle";
import { playClick } from "@/lib/sound";

export default function Grimoire() {
  const { t, i18n } = useTranslation();
  const { player } = usePlayer();
  const unlocked = new Set(player?.grimoire_pages || []);
  const [openPage, setOpenPage] = useState(null);
  const [aiImages, setAiImages] = useState({});
  const [aiLoading, setAiLoading] = useState({});
  const [aiError, setAiError] = useState({});

  const handleGenerate = async (pageId) => {
    if (!player?.name) return;
    setAiLoading((p) => ({ ...p, [pageId]: true }));
    setAiError((p) => ({ ...p, [pageId]: null }));
    try {
      const res = await generateGrimoirePage(pageId, player.name, player.id, i18n.language?.slice(0, 2));
      setAiImages((p) => ({ ...p, [pageId]: res.image_base64 }));
      if (!res.cached) toast.success(t("grimoire.generate_success"));
    } catch (e) {
      const detail = e?.response?.data?.detail || t("grimoire.generate_error");
      setAiError((p) => ({ ...p, [pageId]: detail }));
      toast.error(detail);
    } finally {
      setAiLoading((p) => ({ ...p, [pageId]: false }));
    }
  };

  return (
    <div className="relative min-h-screen hero-radial grain">
      <header className="relative z-30 flex items-center justify-between px-6 sm:px-10 py-5">
        <Link to="/" className="flex items-center gap-2 text-stone-300 hover:text-amber-200" data-testid="back-home">
          <ChevronLeft className="w-4 h-4" /> {t("common.back_home")}
        </Link>
        <div className="flex items-center gap-2 font-serif text-lg">
          <BookOpen className="w-5 h-5 text-amber-300" /> {t("grimoire.header")}
        </div>
        <SoundToggle />
        <LanguageSwitcher />
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-6 sm:px-10 pb-20">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.3em] text-amber-300/90 font-bold">{t("grimoire.kicker")}</div>
          <h2 className="mt-1 font-serif text-4xl sm:text-5xl font-black text-stone-50">
            {t("grimoire.title_1")}<span className="italic text-amber-300">{t("grimoire.title_2")}</span>
          </h2>
          <p className="mt-2 text-stone-300 max-w-2xl">{t("grimoire.subtitle", { unlocked: unlocked.size, total: GRIMOIRE_PAGES.length })}</p>
        </div>

        <div className="grid sm:grid-cols-2 gap-5">
          {GRIMOIRE_PAGES.map((raw, i) => {
            const p = localizeGrimoirePage(raw, t);
            const locked = !unlocked.has(p.id);
            return (
              <motion.button
                key={p.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                onClick={() => !locked && (playClick(), setOpenPage(p))}
                disabled={locked}
                data-testid={`grimoire-card-${p.id}`}
                className={`relative overflow-hidden rounded-3xl border border-white/10 p-6 text-left h-56 ${
                  locked ? "opacity-40" : "hover:-translate-y-1 transition-transform"
                }`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${raw.art_bg}`} aria-hidden />
                <div className="absolute inset-0 bg-black/40" aria-hidden />
                <div className="relative z-10 h-full flex flex-col justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-amber-300/80 font-bold">{t("grimoire.page_prefix", { id: p.id })}</div>
                    <div className="mt-1 font-serif text-2xl font-bold text-stone-50">{p.title}</div>
                  </div>
                  {locked ? (
                    <div className="flex items-center gap-2 text-stone-400 text-sm"><Lock className="w-4 h-4" /> {t("grimoire.locked", { id: p.id })}</div>
                  ) : (
                    <div className="text-sm text-stone-200 italic line-clamp-2">« {p.panels[0]} »</div>
                  )}
                </div>
              </motion.button>
            );
          })}
        </div>
      </main>

      {openPage && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur"
          onClick={() => setOpenPage(null)}
          data-testid="grimoire-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="glass-strong rounded-3xl p-8 max-w-2xl w-full max-h-[85vh] overflow-y-auto"
          >
            <div className="text-xs uppercase tracking-[0.3em] text-amber-300 font-bold">{t("grimoire.page_prefix", { id: openPage.id })}</div>
            <h3 className="mt-1 font-serif text-3xl font-black text-stone-50">{openPage.title}</h3>

            <div className="mt-5">
              {aiImages[openPage.id] ? (
                <img
                  src={`data:image/png;base64,${aiImages[openPage.id]}`}
                  alt={openPage.title}
                  className="w-full rounded-2xl border border-white/10"
                  data-testid={`ai-illustration-${openPage.id}`}
                />
              ) : (
                <div>
                  <button
                    onClick={() => handleGenerate(openPage.id)}
                    disabled={aiLoading[openPage.id]}
                    className="w-full rounded-2xl border-2 border-dashed border-fuchsia-400/40 bg-fuchsia-900/10 hover:bg-fuchsia-900/20 p-6 flex flex-col items-center gap-2 text-fuchsia-200 transition-colors"
                    data-testid={`generate-illustration-${openPage.id}`}
                  >
                    {aiLoading[openPage.id] ? (
                      <>
                        <Loader2 className="w-6 h-6 animate-spin" />
                        <span className="font-serif italic">{t("grimoire.generate_loading", { name: player?.name })}</span>
                      </>
                    ) : (
                      <>
                        <Wand2 className="w-6 h-6" />
                        <span className="font-serif italic">{t("grimoire.generate")}</span>
                        <span className="text-xs text-stone-400">{t("grimoire.generate_hint", { name: player?.name })}</span>
                      </>
                    )}
                  </button>
                  {aiError[openPage.id] && (
                    <div className="mt-3 rounded-2xl border border-amber-400/40 bg-amber-900/10 p-3 text-sm text-amber-100" data-testid={`ai-error-${openPage.id}`}>
                      {aiError[openPage.id]}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="mt-6 space-y-4">
              {openPage.panels.map((panel, i) => (
                <motion.p
                  key={i}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.15 }}
                  className="font-serif text-lg leading-relaxed text-stone-100 border-l-2 border-amber-400/50 pl-4"
                >{panel}</motion.p>
              ))}
            </div>
            <button
              onClick={() => setOpenPage(null)}
              className="mt-6 text-sm text-amber-300 hover:text-amber-200"
              data-testid="grimoire-close"
            >{t("grimoire.close")}</button>
          </div>
        </motion.div>
      )}
    </div>
  );
}
